import React, { useState, useMemo, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import Breadcrumb from "components/Common/Breadcrumb"
import {
  Card, CardBody, Table, Input, Button, Badge,
  Modal, ModalHeader, ModalBody,
} from "reactstrap"
import { Switch } from "antd"
import { FaEdit, FaEye, FaFileExcel, FaPlus, FaUpload } from "react-icons/fa"
import { MdDelete } from "react-icons/md"
import CustomerModal from "./CustomerModal"
import UploadDocumentModal from "./UploadDocumentModal"
import ImportSheet from "./ImportSheet"
import Swal from "sweetalert2"
import Loader from "components/Loader/loader"
import Configs, { axiosbaseurl } from "../../config"
import {
  fetchData,
  FetchDeleteData,
  FetchPostData,
  FetchPutData,
} from "utils/Common_api_functions"
import { usePermissionData } from "AppContext"
import PermittedComponent from "PermittedComponent"

const CustomerIndex = () => {
  document.title = "Customers | Siddhi Tax Consultant"

  const { checkAccess } = usePermissionData()
  const navigate = useNavigate()

  // Pagination + search
  const [pageSize, setPageSize] = useState(20)
  const [searchString, setSearchString] = useState("")
  const [loading, setLoading] = useState(false)
  const [customersData, setCustomersData] = useState([])
  const [customerDocuments, setCustomerDocuments] = useState([])

  // Modal state
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedCustomer, setSelectedCustomer] = useState({})
  const [editingId, setEditingId] = useState(null)
  const [viewModal, setViewModal] = useState(false)
  const [uploadModal, setUploadModal] = useState(false)
  const [uploadTarget, setUploadTarget] = useState(null)
  const [importModal, setImportModal] = useState(false)

  // Remaining fees modal (shown when turning feesPaid OFF)
  const [feesModal, setFeesModal] = useState(false)
  const [feesModalTarget, setFeesModalTarget] = useState(null)
  const [feesRemainingInput, setFeesRemainingInput] = useState("")

  useEffect(() => {
    getCustomers()
    getCustomerDocuments()
  }, [])

  async function getCustomers() {
    setLoading(true)
    try {
      let allData = []
      let offset = 0
      const limit = 50

      while (true) {
        const res = await axiosbaseurl.get(`/customer?limit=${limit}&offset=${offset}`)
        const APIdata = res.data
        if (!APIdata?.success) break
        allData = [...allData, ...(APIdata.data || [])]
        if (!APIdata.paging?.next) break
        offset += limit
      }

      setCustomersData(allData)
    } catch (e) {
      console.error(e)
    }
    setLoading(false)
  }

  async function getCustomerDocuments() {
    const APIdata = await fetchData(setLoading, "document-types")
    if (APIdata?.success) {
      setCustomerDocuments(APIdata?.data || [])
    }
  }

  // Filtered data
  const filteredData = useMemo(() => {
    const q = searchString.toLowerCase()
    return customersData.filter(
      item =>
        item?.customerName?.toLowerCase().includes(q) ||
        item?.phone?.includes(searchString) ||
        item?.members?.some(m => m?.name?.toLowerCase().includes(q))
    )
  }, [customersData, searchString])

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const totalPages = Math.ceil(filteredData.length / pageSize)
  const start = (currentPage - 1) * pageSize

  const paginatedData = useMemo(() => {
    return filteredData.slice(start, start + pageSize)
  }, [filteredData, currentPage, pageSize])

  const handleSubmit = async payload => {
    if (editingId) {
      const APIdata = await FetchPutData(
        setLoading,
        "customer",
        editingId,
        payload
      )
      if (APIdata?.data?.success) {
        Swal.fire({
          title: "Customer Updated Successfully.",
          text: APIdata?.data?.message,
          icon: "success",
        })
        setModalOpen(false)
        setEditingId(null)
        getCustomers()
        setSelectedCustomer({})
      } else {
        Swal.fire({
          title: "Error Updating Customer.",
          text: APIdata?.response?.data?.message,
          icon: "error",
        })
      }
    } else {
      const APIdata = await FetchPostData(setLoading, "customer", payload)
      if (APIdata?.data?.success) {
        Swal.fire({
          title: "Customer Added Successfully.",
          text: APIdata?.data?.message,
          icon: "success",
        })
        setModalOpen(false)
        setSelectedCustomer({})
        getCustomers()
      } else {
        Swal.fire({
          title: "Error Adding Customer.",
          text: APIdata?.response?.data?.message,
          icon: "error",
        })
      }
    }
  }

  const handleDelete = customer => {
    Swal.fire({
      title: `Are you sure you want to delete ${customer?.customerName}?`,
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(async result => {
      if (result.isConfirmed) {
        const APIdata = await FetchDeleteData(
          setLoading,
          "customer",
          customer?.id
        )
        if (APIdata?.data?.success) {
          Swal.fire({
            title: "Customer Deleted Successfully.",
            text: APIdata?.data?.message,
            icon: "success",
          })
          getCustomers()
        } else {
          Swal.fire({
            title: "Error Deleting Customer.",
            text: APIdata?.response?.data?.message,
            icon: "error",
          })
        }
      }
    })
  }

  const handleUpload = async docData => {
    const APIdata = await FetchPostData(setLoading, "customer-documents", docData)
    if (APIdata?.data?.success) {
      Swal.fire({ title: "Documents Uploaded Successfully.", icon: "success" })
    } else {
      Swal.fire({ title: "Error Uploading Documents.", icon: "error" })
    }
    getCustomerDocuments()
  }

  const handleImport = async formData => {
    const APIdata = await FetchPostData(
      setLoading,
      "customer/sheet/import",
      formData
    )
    if (APIdata?.data?.success) {
      Swal.fire({
        title: "Customers Imported Successfully.",
        text: APIdata?.data?.message,
        icon: "success",
      })
      getCustomers()
    } else {
      Swal.fire({
        title: "Error Importing Customers.",
        text: APIdata?.response?.data?.message,
        icon: "error",
      })
    }
  }

  const toggleFeesPaid = async (id, checked) => {
    if (!checked) {
      // Turning OFF: open modal to collect remaining fees
      setFeesModalTarget(id)
      setFeesRemainingInput("")
      setFeesModal(true)
      return
    }

    // Turning ON: simple confirmation
    Swal.fire({
      title: "Are you sure you want to mark fees as Paid?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, proceed!",
      cancelButtonText: "Cancel",
    }).then(async result => {
      if (result.isConfirmed) {
        const APIdata = await FetchPutData(setLoading, "customer", id, {
          feesPaid: true,
        })
        if (APIdata?.data?.success) {
          Swal.fire({
            title: "Fees marked as Paid Successfully.",
            text: APIdata?.data?.message,
            icon: "success",
          })
          getCustomers()
        } else {
          Swal.fire({
            title: "Error Updating Customer.",
            text: APIdata?.response?.data?.message,
            icon: "error",
          })
        }
      }
    })
  }

  const handleFeesModalSubmit = async () => {
    if (feesRemainingInput === "" || isNaN(feesRemainingInput)) {
      Swal.fire({ title: "Please enter a valid remaining fees amount.", icon: "warning" })
      return
    }
    const APIdata = await FetchPutData(setLoading, "customer", feesModalTarget, {
      feesPaid: false,
      feesRemaining: Number(feesRemainingInput),
    })
    if (APIdata?.data?.success) {
      Swal.fire({
        title: "Fees marked as Unpaid Successfully.",
        text: APIdata?.data?.message,
        icon: "success",
      })
      setFeesModal(false)
      getCustomers()
    } else {
      Swal.fire({
        title: "Error Updating Customer.",
        text: APIdata?.response?.data?.message,
        icon: "error",
      })
    }
  }

  const toggleSpPermission = async (id, checked) => {
    Swal.fire({
      title: `Are you sure you want to ${checked ? "Enable" : "Disable"} Special Permission of this Customer?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, proceed!",
      cancelButtonText: "Cancel",
    }).then(async result => {
      if (result.isConfirmed) {
        const APIdata = await FetchPutData(setLoading, "customer", id, {
          spPermission: checked,
        })
        if (APIdata?.data?.success) {
          Swal.fire({
            title: `Special Permission of Customer ${checked ? "Enabled" : "Disabled"} Successfully.`,
            text: APIdata?.data?.message,
            icon: "success",
          })
          getCustomers()
        } else {
          Swal.fire({
            title: "Error Updating Customer.",
            text: APIdata?.response?.data?.message,
            icon: "error",
          })
        }
      }
    })
  }

  const toggleStatus = async (id, checked) => {
    Swal.fire({
      title: `Are you sure you want to ${checked ? "Enable" : "Disable"} this Customer?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, proceed!",
      cancelButtonText: "Cancel",
    }).then(async result => {
      if (result.isConfirmed) {
        const APIdata = await FetchPutData(setLoading, "customer", id, {
          status: checked,
        })
        if (APIdata?.data?.success) {
          Swal.fire({
            title: `Customer ${checked ? "Enabled" : "Disabled"} Successfully.`,
            text: APIdata?.data?.message,
            icon: "success",
          })
          getCustomers()
        } else {
          Swal.fire({
            title: "Error Updating Customer.",
            text: APIdata?.response?.data?.message,
            icon: "error",
          })
        }
      }
    })
  }

  const getDocCount = customerId =>
    customerDocuments?.filter(d => String(d.customerId) === String(customerId))
      .length || 0

  return (
    <PermittedComponent isAccess={checkAccess(4, "view") || checkAccess(4, "viewglobal")}>
    <div className="page-content">
      {loading && <Loader />}
      <Breadcrumb title="Dashboard" breadcrumbItem="Customers" />

      <Card className="shadow-lg">
        <CardBody>
          {/* Top controls */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="d-flex gap-3">
              <Input
                type="select"
                value={pageSize}
                onChange={e => {
                  setPageSize(Number(e.target.value))
                  setCurrentPage(1)
                }}
                className="rounded-4 w-auto pe-5"
              >
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </Input>

              <Input
                className="rounded-4"
                placeholder="Search by name, phone or member..."
                value={searchString}
                onChange={e => setSearchString(e.target.value)}
              />
            </div>

            <div className="gap-2 d-flex">
              {checkAccess(4, "add") && (
              <Button
                className="btn-brand rounded-4 px-3 shadow-lg"
                onClick={() => setImportModal(true)}
              >
                <FaUpload className="me-2" />
                Import
              </Button>
              )}

              {checkAccess(4, "add") && (
              <Button
                className="btn-blue rounded-4 px-3 shadow-lg"
                onClick={() => {
                  setSelectedCustomer({})
                  setEditingId(null)
                  setViewModal(false)
                  setModalOpen(true)
                }}
              >
                <FaPlus className="me-2" />
                Add Customer
              </Button>
              )}

              {(checkAccess(4, "view") || checkAccess(4, "viewglobal")) && (
              <a href={`${process.env.REACT_APP_API_URL}/customer/export/csv`}>
                <Button className="btn-info rounded-4 px-3 shadow-lg">
                  <FaFileExcel className="me-2" />
                  Export
                </Button>
              </a>
              )}
            </div>
          </div>

          {/* Table */}
          <Table bordered responsive>
            <thead className="table-dark">
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Phone</th>
                {/* <th>Address</th> */}
                <th>Members</th>
                {/* <th className="text-center">Docs</th> */}
                <th className="text-center">Fees Paid</th>
                <th className="text-center">Fees Remaining</th>
                <th className="text-center">Status</th>
                <th className="text-center">Special Permissions</th>
                <th className="text-center">Upload Doc</th>
                <th className="text-center">Action</th>
              </tr>
            </thead>

            <tbody>
              {paginatedData.length === 0 ? (
                <tr>
                  <td colSpan="10" className="text-center">
                    No customers found
                  </td>
                </tr>
              ) : (
                paginatedData.map((item, index) => (
                  <tr key={item.id}>
                    <td>{start + index + 1}</td>
                    <td className="fw-bold">{item?.customerName}</td>
                    <td>{item?.phone}</td>
                    {/* <td
                      style={{
                        maxWidth: 180,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {item?.address || "—"}
                    </td> */}
                    <td>
                      {item?.members?.length > 0 ? (
                        <div className="d-flex flex-wrap gap-1">
                          {item.members.map((m, i) => (
                            <span
                              key={i}
                              className="badge rounded-1 fs-6 bg-blue text-white me-1 mb-1 px-2 py-1 text-wrap"
                            >
                              {m.name}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted">No members</span>
                      )}
                    </td>
                    {/* <td className="text-center">
                      <span className="badge bg-success fs-6">
                        {getDocCount(item.id)}
                      </span>
                    </td> */}
                    <td className="text-center">
                      <Switch
                        disabled={!checkAccess(4, "edit")}
                        checked={item.feesPaid}
                        onChange={checked => toggleFeesPaid(item.id, checked)}
                      />
                    </td>
                    <td style={{textAlign: 'center'}}>₹ {item.feesRemaining || 0} </td>
                    
                    <td className="text-center">
                      <Switch
                        disabled={!checkAccess(4, "edit")}
                        checked={item.status}
                        onChange={checked => toggleStatus(item.id, checked)}
                      />
                    </td>
                    <td className="text-center">
                      <Switch
                        disabled={!checkAccess(4, "edit")}
                        checked={item.spPermission}
                        onChange={checked => toggleSpPermission(item.id, checked)}
                      />
                    </td>
                    <td className="text-center">
                      {checkAccess(4, "edit") && (
                      <Button
                        className="bg-blue text-white border-0 shadow-none d-flex align-items-center gap-1 mx-auto"
                        disabled={!item.status}
                        onClick={() => {
                          setUploadTarget(item)
                          setUploadModal(true)
                        }}
                      >
                        <FaUpload size={13} /> Upload
                      </Button>
                      )}
                    </td>
                    <td className="d-flex flex-wrap-none justify-content-center">
                      {(checkAccess(4, "view") || checkAccess(4, "viewglobal")) && (
                      <Button
                        className="bg-secondary text-white me-2 border-0 shadow-none"
                        onClick={() =>
                          navigate(`${Configs.appurl}customers/${item.id}`)
                        }
                      >
                        <FaEye size={16} />
                      </Button>
                      )}
                      {checkAccess(4, "edit") && (
                      <Button
                        className="bg-success text-white me-2 border-0 shadow-none"
                        onClick={async () => {
                          setEditingId(item.id)
                          setViewModal(false)
                          setSelectedCustomer({})
                          setModalOpen(true)
                          const APIdata = await fetchData(
                            setLoading,
                            `customer/${item.id}`
                          )
                          setSelectedCustomer(
                            APIdata?.success ? APIdata.data : { ...item }
                          )
                        }}
                      >
                        <FaEdit size={16} />
                      </Button>
                      )}
                      {checkAccess(4, "delete") && (
                      <Button
                        className="bg-danger text-white border-0 shadow-none"
                        onClick={() => handleDelete(item)}
                      >
                        <MdDelete size={18} />
                      </Button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          {/* Pagination */}
          <nav
            aria-label="Page navigation"
            className="d-flex justify-content-between align-items-center mt-3"
          >
            <h5 className="mb-0">
              Showing {start + 1} to{" "}
              {Math.min(start + pageSize, filteredData.length)} of{" "}
              {customersData.length} entries
            </h5>
            <ul className="pagination mb-0">
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                >
                  Previous
                </button>
              </li>

              {[...Array(totalPages)].map((_, i) => {
                const pageNum = i + 1
                return (
                  <li
                    key={pageNum}
                    className={`page-item ${
                      pageNum === currentPage ? "active" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  </li>
                )
              })}

              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() =>
                    setCurrentPage(prev => Math.min(prev + 1, totalPages))
                  }
                >
                  Next
                </button>
              </li>
            </ul>
          </nav>
        </CardBody>
      </Card>

      <CustomerModal
        open={modalOpen}
        toggle={() => setModalOpen(false)}
        selectedCustomer={selectedCustomer}
        setSelectedCustomer={setSelectedCustomer}
        viewOnly={viewModal}
        onSubmit={handleSubmit}
      />

      <UploadDocumentModal
        open={uploadModal}
        toggle={() => setUploadModal(false)}
        customer={uploadTarget}
        onUpload={handleUpload}
      />

      <ImportSheet
        open={importModal}
        toggle={() => setImportModal(false)}
        onUpload={handleImport}
      />

      <Modal size="sm" isOpen={feesModal} toggle={() => setFeesModal(false)} centered>
        <ModalHeader toggle={() => setFeesModal(false)}>
          Enter Fees
          <div>
            <Button className="modal-btn upload-btn" onClick={handleFeesModalSubmit}>
              Confirm
            </Button>
          </div>
        </ModalHeader>
        <ModalBody>
          <label className="form-label fw-semibold">Remaining Fees (₹)</label>
          <Input
            type="number"
            min="0"
            placeholder="Enter remaining fees amount"
            value={feesRemainingInput}
            onChange={e => setFeesRemainingInput(e.target.value)}
          />
        </ModalBody>
      </Modal>
    </div>
    </PermittedComponent>
  )
}

export default CustomerIndex
